import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useRouter } from "expo-router";
import { MaterialIcons } from "@expo/vector-icons";

import { DefaultAvatar } from "./Components/DefaultAvatar";

import CompatibilityScores from "@/screens/Explore/Components/ProfileCard/Components/CompatibilityScores";
import CompatibilityHighlights from "@/screens/Explore/Components/ProfileCard/Components/CompatibilityHighlights";
import UserImageLoading from "@/Components/Media/User/Images/UserImageLoading";
import { Spacings } from "@/styling/spacings";
import { themes } from "@/Components/Styling/themeConfigs";
import { UserRecommendedItem } from "@/schemas/user/explore";
import { ProfileCardState } from "@/utils/explore";
import BorderLayout from "@/Components/Layouts/Native/BorderLayout";

const Z_INDEX = {
  HIGHLIGHTS_SCORES: 2,
  LOCK_ICON: 9,
  SEE_ALL_OVERLAY: 10, // above all
} as const;

type Props = {
  profile: UserRecommendedItem;
  itemWidth: number;
  itemHeight: number;
  displayHighlights?: boolean;
  isLargeCard?: boolean;
  children: React.ReactNode;
  cardState?: ProfileCardState;
  onNonNormalStateCardClick?: () => void;
};

function BaseProfileCard({
  profile,
  itemWidth,
  itemHeight,
  displayHighlights = true,
  isLargeCard = false,
  children,
  cardState = "normal",
  onNonNormalStateCardClick = (): void => {},
}: Props): React.JSX.Element {
  const router = useRouter();
  const hasSeeAllOverlay = cardState === "overlay";
  const isLocked = cardState === "locked";

  const handleProfilePress = (): void => {
    // Only navigate for normal state, otherwise delegate to parent component with onNonNormalStateCardClick
    if (cardState === "normal") {
      router.push({
        params: { username: profile.username },
        pathname: "/(main)/shared/profile",
      });
    } else {
      onNonNormalStateCardClick();
    }
  };

  return (
    <BorderLayout
      width={itemWidth}
      height={itemHeight}
      radiusTop={8}
      radiusBottom={8}
    >
      <Pressable testID="card-touchable" onPress={handleProfilePress}>
        {/* see all overlay */}
        {hasSeeAllOverlay && (
          <View
            style={[
              styles.seeAllOverlayContainer,
              styles.semiTransparentBackgroundColor,
            ]}
          >
            <View style={styles.roundedPillContainer}>
              <Text
                style={[
                  styles.seeAllText,
                  {
                    fontSize: isLargeCard
                      ? currentTheme.typography.body.size.mobile + 2
                      : currentTheme.typography.body.size.mobile,
                  },
                ]}
              >
                See All
              </Text>
            </View>
          </View>
        )}

        {/* lock icon */}
        {isLocked && (
          <View style={styles.lockIconContainer}>
            <View
              style={[
                styles.lockIconOverlay,
                styles.semiTransparentBackgroundColor,
                isLargeCard
                  ? styles.lockIconOverlayDimensionsLarge
                  : styles.lockIconOverlayDimensionsSmall,
              ]}
            >
              <MaterialIcons
                name="lock-outline"
                size={isLargeCard ? 24 : 18}
                color="#fff"
              />
            </View>
          </View>
        )}

        {/* user profile image */}
        {profile.image ? (
          <UserImageLoading
            key={profile.image.attributes.previewUrl}
            imageUrl={profile.image.attributes.previewUrl}
            width={itemWidth}
            height={itemHeight}
          />
        ) : (
          <DefaultAvatar width={itemWidth} height={itemHeight} />
        )}

        {/* card top highlights and scores */}
        <View
          style={[
            styles.highlightsScoresContainer,
            isLargeCard
              ? styles.highlightsScoresContainerSpacingLarge
              : styles.highlightsScoresContainerSpacingSmall,
            (!displayHighlights || profile.highlights.length === 0) && {
              justifyContent: "flex-end", // Adjust alignment if no highlights or highlights disabled
            },
          ]}
        >
          {/* highlights */}
          {displayHighlights && profile.highlights.length > 0 && (
            <CompatibilityHighlights
              highlights={profile.highlights}
              isLargeCard={isLargeCard}
            />
          )}

          {/* compatibility scores */}
          {profile.scores.length > 0 && (
            <CompatibilityScores
              compatibility={profile.scores}
              isLargeCard={isLargeCard}
            />
          )}
        </View>
        {children}
      </Pressable>
    </BorderLayout>
  );
}

const currentTheme = themes.morning;

const styles = StyleSheet.create({
  // see all styles
  seeAllOverlayContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center", // This centers content horizontally
    zIndex: Z_INDEX.SEE_ALL_OVERLAY,
  },
  roundedPillContainer: {
    borderRadius: 30,
    paddingVertical: Spacings.s2,
    paddingHorizontal: Spacings.s3,
    marginBottom: Spacings.s3,
    alignItems: "center",
    backgroundColor: "rgba(85, 85, 85, 0.75)",
  },
  seeAllText: {
    color: "#eee",
    fontSize: currentTheme.typography.body.size.mobile + 2,
    fontWeight: "bold",
  },
  // lock icon styles
  lockIconContainer: {
    position: "absolute",
    top: Spacings.s2,
    left: Spacings.s2,
    zIndex: Z_INDEX.LOCK_ICON,
  },
  lockIconOverlay: {
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  lockIconOverlayDimensionsSmall: {
    width: 32,
    height: 32,
  },
  lockIconOverlayDimensionsLarge: {
    width: 48,
    height: 48,
  },
  // highlights + scores container
  highlightsScoresContainer: {
    position: "absolute",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    zIndex: Z_INDEX.HIGHLIGHTS_SCORES,
  },
  highlightsScoresContainerSpacingSmall: {
    top: Spacings.s1,
    left: Spacings.s1,
    right: Spacings.s1,
  },
  highlightsScoresContainerSpacingLarge: {
    top: Spacings.s2,
    left: Spacings.s2,
    right: Spacings.s2,
  },
  // shared styles
  semiTransparentBackgroundColor: {
    backgroundColor: "rgba(0, 0, 0, 0.50)",
  },
});

export default BaseProfileCard;
